/**
 * Orda OS — typed DB error layer.
 *
 * Цель: клиент НЕ парсит текст ошибки. Парсит errcode (P0xxx) → enum.
 * UI показывает локализованное сообщение по enum.
 *
 * Race-swallow pattern: некоторые errcodes — это "expected race" (повар B
 * нажал ready после повара A). Их НЕ показывать как ошибку. Realtime
 * приносит fresh state, UI синхронизируется автоматически.
 *
 * Использование:
 *   try {
 *     await callRpc('update_kitchen_status', () =>
 *       supabase.rpc('update_kitchen_status', { p_order, p_status }),
 *       { swallowRace: ['ORDER_TERMINAL', 'INVALID_TRANSITION'] },
 *     );
 *   } catch (err) {
 *     if (err instanceof DbError) {
 *       showToast(DB_ERROR_MESSAGE[err.code] ?? err.message);
 *     }
 *   }
 */

// ────────────────────────────────────────────────────────────────────────────
// Error type system
// ────────────────────────────────────────────────────────────────────────────

/**
 * Orda OS error codes. Соответствуют CONTRACT.md § 5 Errcodes.
 * 'UNKNOWN' — для PostgreSQL ошибок не из нашего набора (network, syntax, ...).
 */
export type ErrCode =
  // Idempotency / system
  | 'IDEMPOTENCY_IN_PROGRESS'        // P0010
  | 'IDEMPOTENCY_BUG'                // P0011
  // Cart / order request
  | 'CART_EMPTY'                     // P0020
  | 'SESSION_NOT_OPEN'               // P0021
  | 'SESSION_NOT_FOUND'              // P0022
  | 'ORDER_TOTAL_INVALID'            // P0023
  | 'MENU_ITEM_UNAVAILABLE'          // P0024 — блюдо неактивно/стоп-лист/не из меню тенанта (00384)
  // Payment / refund
  | 'SESSION_PAID_IN_FULL'           // P0030
  | 'REFUND_EXCEEDS_PAYMENT'         // P0040
  | 'PAYMENT_NOT_FOUND'              // P0041
  // Validation
  | 'INVALID_ARGUMENT'               // P0050
  | 'IDEMPOTENCY_KEY_REQUIRED'       // P0051
  // Lifecycle (session/table)
  | 'TABLE_NOT_FOUND'                // P0060
  | 'INVALID_TOKEN'                  // P0061
  | 'SESSION_NOT_BALANCED'           // P0062
  | 'KITCHEN_ORDERS_ACTIVE'          // P0063
  | 'INCONSISTENT_STATE'             // P0064
  | 'SESSION_CORRUPTED'              // P0065
  | 'TARGET_TABLE_OCCUPIED'          // P0066
  | 'ABANDONED_REASON_REQUIRED'      // P0067
  | 'FORBIDDEN'                      // P0068
  | 'CART_HAS_UNPLACED_ITEMS'        // P0069
  // Kitchen
  | 'ORDER_NOT_FOUND'                // P0070
  | 'ORDER_TERMINAL'                 // P0071
  | 'INVALID_KITCHEN_TRANSITION'     // P0072
  | 'CANNOT_CANCEL_CLOSED'           // P0073
  // Per-station kitchen lines (00238)
  | 'STALE_LINE_VERSION'             // P0074 — optimistic concurrency: строка изменилась, refetch
  | 'ROLLUP_ZERO_LINES'             // P0075 — заказ без строк (коррупция)
  | 'KITCHEN_ALREADY_ACCEPTED'       // P0076 — нельзя отменить строку: кухня уже приняла (00250)
  // Cart-line operations
  | 'CART_LINE_NOT_FOUND'            // P0080
  | 'LINE_HAS_PENDING_TRANSFER'      // P0081
  | 'NO_ACTIVE_SHARE_VOTE'           // P0082
  | 'CANNOT_VOTE_OWN_REQUEST'        // P0083
  | 'VOTER_BLOCKED'                  // P0084
  | 'SHARE_VOTE_EXPIRED'             // P0085
  | 'ONLY_INITIATOR_CAN_CANCEL'      // P0086
  | 'LINE_HAS_PENDING_VOTE'          // P0087
  // Security
  | 'MODEL_3D_PROTECTED'             // P0090
  | 'TENANT_SUSPENDED'               // P0091 — кафе приостановлено super-admin
  // Phase F — waiter business RPCs (00025..00029)
  | 'OTHER_WAITER_HOLDS'             // P0102 — release/transfer on session held by another waiter
  | 'INVALID_TRANSFER_TARGET'        // P0103 — self-target / inactive / wrong role / cross-tenant target / pooled session
  | 'SERVICE_REQUEST_NOT_FOUND'      // P0110
  | 'INVALID_REQUEST_TRANSITION'     // P0111 — acknowledge on already-resolved
  // Kaspi-сверка (00179)
  | 'CLAIM_CHANGED'                  // P0112 — сумма заявки изменилась после просмотра кассиром
  // Tokenless QR (00260): повторный гость заказал без подтверждения присутствия
  | 'SESSION_REQUIRES_ACK'           // P0113 — готовка заблокирована, пока официант
                                     //         не подтвердил присутствие гостя за столом
  // SEC-2026-06-15 follow-up (00229/00230/00234)
  | 'RPC_RATE_LIMITED'               // P0099 — per-tenant rate-limit на add_payment/place_order (MED-4)
  | 'SESSION_REVOKED'                // P0100 — require_active_staff: auth-сессия отозвана,
                                     //         нужно перелогиниться. Установлено HIGH-3 (00234).
  | 'DEVICE_SESSION_ACTIVE'          // P0114 — admin_forget_device: нельзя забыть устройство
                                     //         с живой сессией, сначала «Завершить сессию» (00265).
  | 'DELIVERY_NOT_ENABLED'           // P0115 — тариф тенанта не включает доставку
  | 'INVALID_DISH_OPTIONS'           // P0116 — выбор опций блюда не прошёл серверную валидацию (00466)
  // Fallback
  | 'UNKNOWN';

/** SQLSTATE → ErrCode mapping. */
const PG_CODE_TO_ERR_CODE: Record<string, ErrCode> = {
  P0010: 'IDEMPOTENCY_IN_PROGRESS',
  P0011: 'IDEMPOTENCY_BUG',
  P0020: 'CART_EMPTY',
  P0021: 'SESSION_NOT_OPEN',
  P0022: 'SESSION_NOT_FOUND',
  P0023: 'ORDER_TOTAL_INVALID',
  P0024: 'MENU_ITEM_UNAVAILABLE',
  P0030: 'SESSION_PAID_IN_FULL',
  P0040: 'REFUND_EXCEEDS_PAYMENT',
  P0041: 'PAYMENT_NOT_FOUND',
  P0050: 'INVALID_ARGUMENT',
  P0051: 'IDEMPOTENCY_KEY_REQUIRED',
  P0060: 'TABLE_NOT_FOUND',
  P0061: 'INVALID_TOKEN',
  P0062: 'SESSION_NOT_BALANCED',
  P0063: 'KITCHEN_ORDERS_ACTIVE',
  P0064: 'INCONSISTENT_STATE',
  P0065: 'SESSION_CORRUPTED',
  P0066: 'TARGET_TABLE_OCCUPIED',
  P0067: 'ABANDONED_REASON_REQUIRED',
  P0068: 'FORBIDDEN',
  P0069: 'CART_HAS_UNPLACED_ITEMS',
  P0070: 'ORDER_NOT_FOUND',
  P0071: 'ORDER_TERMINAL',
  P0072: 'INVALID_KITCHEN_TRANSITION',
  P0073: 'CANNOT_CANCEL_CLOSED',
  P0074: 'STALE_LINE_VERSION',
  P0075: 'ROLLUP_ZERO_LINES',
  P0076: 'KITCHEN_ALREADY_ACCEPTED',
  P0080: 'CART_LINE_NOT_FOUND',
  P0081: 'LINE_HAS_PENDING_TRANSFER',
  P0082: 'NO_ACTIVE_SHARE_VOTE',
  P0083: 'CANNOT_VOTE_OWN_REQUEST',
  P0084: 'VOTER_BLOCKED',
  P0085: 'SHARE_VOTE_EXPIRED',
  P0086: 'ONLY_INITIATOR_CAN_CANCEL',
  P0087: 'LINE_HAS_PENDING_VOTE',
  P0090: 'MODEL_3D_PROTECTED',
  P0091: 'TENANT_SUSPENDED',
  // Phase F (00025..00029)
  P0102: 'OTHER_WAITER_HOLDS',
  P0103: 'INVALID_TRANSFER_TARGET',
  P0110: 'SERVICE_REQUEST_NOT_FOUND',
  P0111: 'INVALID_REQUEST_TRANSITION',
  // Kaspi-сверка (00179)
  P0112: 'CLAIM_CHANGED',
  // Tokenless QR (00260) — триггеры block_cook_until_ack_{order,line}
  P0113: 'SESSION_REQUIRES_ACK',
  // SEC-2026-06-15 follow-up
  P0098: 'TENANT_SUSPENDED',         // assert_tenant_active() из 00113/00229
                                     // (alias к P0091 — оба = «кафе приостановлено»)
  P0099: 'RPC_RATE_LIMITED',         // 00230 — token-bucket на add_payment/place_order
  P0100: 'SESSION_REVOKED',          // 00234 — require_active_staff
  P0114: 'DEVICE_SESSION_ACTIVE',    // 00265 — admin_forget_device on live session
  P0115: 'DELIVERY_NOT_ENABLED',
  P0116: 'INVALID_DISH_OPTIONS',
};

/**
 * Default human-readable сообщения (русский, fallback).
 * Apps могут переопределить через i18n.
 */
export const DB_ERROR_MESSAGE: Record<ErrCode, string> = {
  IDEMPOTENCY_IN_PROGRESS: 'Операция в процессе. Попробуйте через секунду.',
  IDEMPOTENCY_BUG: 'Внутренняя ошибка. Свяжитесь с поддержкой.',
  CART_EMPTY: 'Корзина пуста',
  SESSION_NOT_OPEN: 'Сессия закрыта',
  SESSION_NOT_FOUND: 'Сессия не найдена',
  ORDER_TOTAL_INVALID: 'Сумма заказа должна быть больше нуля',
  MENU_ITEM_UNAVAILABLE: 'Блюдо недоступно (нет в наличии или снято с меню)',
  SESSION_PAID_IN_FULL: 'Счёт уже оплачен полностью',
  REFUND_EXCEEDS_PAYMENT: 'Возврат превышает сумму платежа',
  PAYMENT_NOT_FOUND: 'Платёж не найден',
  INVALID_ARGUMENT: 'Неверные параметры',
  IDEMPOTENCY_KEY_REQUIRED: 'Внутренняя ошибка: idempotency key required',
  TABLE_NOT_FOUND: 'Стол не найден',
  INVALID_TOKEN: 'QR-код устарел. Отсканируйте снова.',
  SESSION_NOT_BALANCED: 'Счёт не сходится: сумма платежей ≠ сумме заказов',
  KITCHEN_ORDERS_ACTIVE: 'Нельзя закрыть стол — на кухне есть незавершённые заказы',
  INCONSISTENT_STATE: 'Несогласованное состояние стола (свяжитесь с поддержкой)',
  SESSION_CORRUPTED: 'Расхождение сумм в сессии (свяжитесь с поддержкой)',
  TARGET_TABLE_OCCUPIED: 'Целевой стол занят',
  ABANDONED_REASON_REQUIRED: 'Укажите причину закрытия без оплаты',
  FORBIDDEN: 'Недостаточно прав',
  CART_HAS_UNPLACED_ITEMS: 'Нельзя закрыть стол — в корзине есть неоформленные позиции',
  ORDER_NOT_FOUND: 'Заказ не найден',
  ORDER_TERMINAL: 'Заказ уже завершён',
  INVALID_KITCHEN_TRANSITION: 'Недопустимый переход статуса',
  CANNOT_CANCEL_CLOSED: 'Нельзя отменить заказ в закрытой сессии',
  STALE_LINE_VERSION: 'Позиция уже изменилась — обновляем',
  ROLLUP_ZERO_LINES: 'Заказ без позиций (свяжитесь с поддержкой)',
  KITCHEN_ALREADY_ACCEPTED: 'Кухня уже приняла блюдо — отменить нельзя',
  CART_LINE_NOT_FOUND: 'Позиция корзины не найдена',
  LINE_HAS_PENDING_TRANSFER: 'У этой позиции уже есть ожидающая передача',
  NO_ACTIVE_SHARE_VOTE: 'Голосование не запущено',
  CANNOT_VOTE_OWN_REQUEST: 'Инициатор не может голосовать за свой запрос',
  VOTER_BLOCKED: 'Вы не можете голосовать по этой позиции',
  SHARE_VOTE_EXPIRED: 'Голосование истекло',
  ONLY_INITIATOR_CAN_CANCEL: 'Только инициатор может отменить',
  LINE_HAS_PENDING_VOTE: 'У этой позиции уже идёт голосование',
  MODEL_3D_PROTECTED: '3D-модель может изменять только администратор платформы',
  TENANT_SUSPENDED: 'Кафе временно недоступно — приостановлено администратором платформы',
  // Phase F messages
  OTHER_WAITER_HOLDS: 'Стол держит другой официант. Используй force_release (manager+).',
  INVALID_TRANSFER_TARGET: 'Невозможно: целевой официант недоступен или стол в пуле.',
  SERVICE_REQUEST_NOT_FOUND: 'Запрос не найден',
  INVALID_REQUEST_TRANSITION: 'Невозможно: запрос уже закрыт',
  CLAIM_CHANGED: 'Сумма заявки изменилась — проверь обновлённую и подтверди заново',
  SESSION_REQUIRES_ACK: 'Стол ждёт подтверждения присутствия официантом — готовка пока заблокирована',
  // SEC-2026-06-15 follow-up
  RPC_RATE_LIMITED: 'Слишком много операций — подожди минуту и попробуй ещё раз',
  SESSION_REVOKED: 'Сессия отозвана администратором. Войди заново.',
  DEVICE_SESSION_ACTIVE: 'Сначала завершите сессию устройства, потом «Забыть».',
  DELIVERY_NOT_ENABLED: 'Доставка для этого кафе недоступна. Выберите самовывоз.',
  INVALID_DISH_OPTIONS: 'Выберите опции блюда (размер / добавки) и попробуйте снова.',
  UNKNOWN: 'Неизвестная ошибка базы данных',
};

/**
 * Typed Orda OS error.
 * `code` — наш enum, `message` — server message, `raw` — original PostgrestError.
 */
export class DbError extends Error {
  public readonly code: ErrCode;
  public readonly raw: unknown;

  constructor(code: ErrCode, message: string, raw?: unknown) {
    super(message);
    this.name = 'DbError';
    this.code = code;
    this.raw = raw;
  }

  /** Локализованное сообщение из DB_ERROR_MESSAGE. */
  get localizedMessage(): string {
    return DB_ERROR_MESSAGE[this.code];
  }

  /**
   * true = ожидаемое доменное ограничение (USER_INPUT_CODES), не баг.
   * Публичный контракт для observability: beforeSend читает флаг duck-typing'ом
   * (name === 'DbError' && isExpected === true) и понижает событие до level:info,
   * если такая ошибка ушла в Sentry мимо callRpc (unhandled rejection).
   */
  get isExpected(): boolean {
    return USER_INPUT_CODES.has(this.code);
  }
}

// ────────────────────────────────────────────────────────────────────────────
// Parser
// ────────────────────────────────────────────────────────────────────────────

interface PostgrestErrorShape {
  code?: string;       // SQLSTATE
  message?: string;
  details?: string;
  hint?: string;
  /** Edge Function errors могут вкладывать в .error: { code, message }. */
  error?: { code?: string; message?: string };
}

/**
 * Парсит PostgrestError / FunctionsError / Error → DbError.
 * Поддерживает nested shape: { error: { code, message } } — Edge Function
 * через invoke() оборачивает PostgrestError именно так.
 * Возвращает null если raw — не ошибка БД.
 *
 * raw ВСЕГДА сохраняется в DbError.raw — для дебага не теряем SQLSTATE/details/hint.
 */
export function parseDbError(raw: unknown): DbError | null {
  if (!raw) return null;
  if (raw instanceof DbError) return raw;

  if (typeof raw === 'object') {
    const obj = raw as PostgrestErrorShape;
    // Top-level code, иначе nested .error.code (Edge Function wrapper).
    const code = obj.code ?? obj.error?.code;
    const message = obj.message ?? obj.error?.message ?? 'DB error';
    if (typeof code === 'string') {
      // Direct SQLSTATE → ErrCode mapping (P00xx codes).
      let errCode: ErrCode = PG_CODE_TO_ERR_CODE[code] ?? 'UNKNOWN';
      // Fallback: PostgREST custom-status codes (PT4xx → HTTP 4xx) carry
      // the application errcode in the `hint` field. See migration
      // 00048_token_error_4xx.sql for the canonical pattern.
      if (errCode === 'UNKNOWN' && code.startsWith('PT') && typeof obj.hint === 'string') {
        const hintCode = PG_CODE_TO_ERR_CODE[obj.hint];
        if (hintCode) errCode = hintCode;
      }
      return new DbError(errCode, message, raw);
    }
    if (typeof obj.message === 'string') {
      return new DbError('UNKNOWN', obj.message, raw);
    }
  }

  if (raw instanceof Error) {
    return new DbError('UNKNOWN', raw.message, raw);
  }

  return null;
}

// ────────────────────────────────────────────────────────────────────────────
// Race-swallow helpers
// ────────────────────────────────────────────────────────────────────────────

export interface CallRpcOptions {
  /**
   * Если RPC вернёт error с одним из этих кодов — НЕ throw'аем,
   * возвращаем null. Реалтайм синхронизирует UI.
   * Пример: ['ORDER_TERMINAL', 'INVALID_KITCHEN_TRANSITION']
   * для update_kitchen_status — повар B нажал после повара A.
   */
  swallowRace?: ErrCode[];
  /**
   * Best-effort вызовы (presence-heartbeat и т.п.): НЕ репортить ошибку в
   * Sentry/Telegram. Throw остаётся (вызывающий сам решает что делать), но
   * алерт не шлём. Нужно для fire-and-forget пингов, которые по дизайну
   * могут упасть в гоночном окне (напр. heartbeat под `anon` пока новый staff
   * JWT ещё не сел на клиент) — это ожидаемо и self-healing на следующем тике,
   * пейджить дежурного по такому не нужно.
   */
  silent?: boolean;
}

export interface SupabaseRpcResult<T> {
  data: T | null;
  error: unknown;
}

/**
 * Wrapper around supabase.rpc() с race-swallow логикой.
 *
 * Returns:
 *   • data (на success или swallow'нутом race — last в случае race null)
 *   • throws DbError если error не в swallowRace
 *
 * Использует единый @ts-expect-error чтобы скрыть строгую типизацию
 * Database.Functions от supabase-js v2 (мы не генерируем types-db.ts
 * через `supabase gen types`, поэтому Args/Returns не известны).
 */
import { getSupabaseClient } from "./client";

/**
 * Коды которые representуют валидные user-input ошибки, не баги.
 * Эти НЕ идут в Sentry — иначе спам когда официант ввёл неверный PIN
 * или гость нажал «Оформить» с пустой корзиной.
 */
const USER_INPUT_CODES = new Set<ErrCode>([
  'CART_EMPTY',
  'INVALID_TOKEN',
  'INVALID_ARGUMENT',
  'IDEMPOTENCY_IN_PROGRESS',
  'IDEMPOTENCY_KEY_REQUIRED',
  'TABLE_NOT_FOUND',
  'SESSION_NOT_FOUND',
  'ORDER_NOT_FOUND',
  'PAYMENT_NOT_FOUND',
  'FORBIDDEN',
  'INVALID_REQUEST_TRANSITION',
  'INVALID_KITCHEN_TRANSITION',
  'ORDER_TERMINAL',
  // STALE_LINE_VERSION — доброкачественная гонка цехов: повар двигал строку,
  // которую другой уже продвинул. Realtime ресинхронизирует, не баг.
  'STALE_LINE_VERSION',
  // KITCHEN_ALREADY_ACCEPTED — официант опоздал отменить: повар принял блюдо.
  // Валидное ограничение, не баг — не пейджим.
  'KITCHEN_ALREADY_ACCEPTED',
  'CART_LINE_NOT_FOUND',
  'CART_HAS_UNPLACED_ITEMS',
  'NO_ACTIVE_SHARE_VOTE',
  'LINE_HAS_PENDING_TRANSFER',
  'LINE_HAS_PENDING_VOTE',
  'CANNOT_VOTE_OWN_REQUEST',
  'VOTER_BLOCKED',
  'SHARE_VOTE_EXPIRED',
  'ONLY_INITIATOR_CAN_CANCEL',
  'INVALID_TRANSFER_TARGET',
  'TARGET_TABLE_OCCUPIED',
  'ABANDONED_REASON_REQUIRED',
  'SERVICE_REQUEST_NOT_FOUND',
  'OTHER_WAITER_HOLDS',
  // SEC-2026-06-15 follow-up: эти коды НЕ баги, а валидные ограничения.
  // RPC_RATE_LIMITED — пользователь слишком быстро тыкает.
  // SESSION_REVOKED — менеджер отозвал — UI должен показать «войди заново».
  'RPC_RATE_LIMITED',
  'SESSION_REVOKED',
  // DEVICE_SESSION_ACTIVE — админ нажал «Забыть» на устройстве с живой сессией.
  // Валидное ограничение (сначала «Завершить сессию»), не баг — не пейджим.
  'DEVICE_SESSION_ACTIVE',
  // Тарифный гейт доставки — ожидаемое бизнес-ограничение, не backend-инцидент.
  'DELIVERY_NOT_ENABLED',
  // SESSION_NOT_OPEN (P0021) — стол уже закрыт. Гость держал открытой вкладку
  // меню, пока за столом рассчитались, и жмёт «добавить»; или касса повторно
  // закрывает уже закрытую сессию. UI показывает сообщение, чинить нечего —
  // раньше это шумело в Sentry (JAVASCRIPT-REACT-23/2A).
  // TENANT_SUSPENDED сюда сознательно НЕ добавлен: приостановленный тенант
  // означает, что живое кафе недоступно гостям — это стоит видеть.
  'SESSION_NOT_OPEN',
  // SESSION_REQUIRES_ACK (P0113) — защита tokenless QR (00260): повар жмёт
  // «готовить», но повторный гость ещё не подтверждён официантом. Сервер
  // легально блокирует, повар видит сообщение и зовёт официанта. Раньше код
  // не был замаплен → UNKNOWN → летел в Sentry как error (JAVASCRIPT-REACT-3F).
  'SESSION_REQUIRES_ACK',
]);

/**
 * Lazy-shipped Sentry capture. Динамический import — packages/db не зависит
 * жёстко от @cafe-os/observability (тесты могут не иметь его в зависимостях).
 * Если observability недоступен — silently noop.
 */
async function reportRpcError(rpcName: string, error: Error, args: Record<string, unknown>): Promise<void> {
  try {
    const obs = await import("@cafe-os/observability");
    obs.captureException(error, {
      rpc: rpcName,
      // Не шлём значения args (могут быть PII: phone, pin, секреты).
      // Шлём только keys — достаточно для диагностики.
      args_keys: Object.keys(args).join(","),
    });
  } catch {
    // observability не установлен (test env / server-side) — silently noop.
  }
}

export async function callRpc<T>(
  rpcName: string,
  args: Record<string, unknown>,
  options: CallRpcOptions = {},
): Promise<T | null> {
  const supabase = getSupabaseClient();
  // @ts-expect-error supabase-js v2 без сгенерированного Database.Functions
  // не пропускает произвольные RPC — мы знаем что вызовы корректны (валидируется
  // на сервере errcode'ами P0050/P0051).
  const { data, error } = (await supabase.rpc(rpcName, args)) as SupabaseRpcResult<T>;

  if (error) {
    const dbError = parseDbError(error);

    // Race-swallow: ignore expected concurrent-action errors — не в Sentry.
    if (dbError && options.swallowRace?.includes(dbError.code)) {
      console.debug(
        `[db/${rpcName}] race swallowed: ${dbError.code} (${dbError.message})`,
      );
      return null;
    }

    // Sentry capture: только настоящие баги / infra failures.
    // User-input ошибки (PIN, INVALID_TOKEN, FORBIDDEN от RLS) — НЕ шлём.
    // silent: best-effort вызов (heartbeat) — throw оставляем, алерт не шлём.
    if (options.silent) {
      // no report — fire-and-forget caller (см. CallRpcOptions.silent)
    } else if (dbError && !USER_INPUT_CODES.has(dbError.code)) {
      void reportRpcError(rpcName, dbError, args);
    } else if (!dbError) {
      // Unknown error type (network failure, postgres syntax, RLS w/o errcode) —
      // всегда захватываем, это либо infra либо bug.
      void reportRpcError(
        rpcName,
        new Error(`[db/${rpcName}] unknown error: ${String(error)}`),
        args,
      );
    }

    // SEC-2026-06-15 HIGH-3 (00234): SESSION_REVOKED означает что менеджер
    // отозвал устройство кассира/официанта. Бросаем глобальное window-событие
    // — SupabaseAuthProvider его перехватывает и делает signOut + redirect на
    // PIN-вход. Это разделяет concern'ы: packages/db не зависит от auth.
    if (dbError?.code === 'SESSION_REVOKED' && typeof window !== 'undefined') {
      try {
        window.dispatchEvent(new CustomEvent('cafeos:session-revoked', {
          detail: { rpc: rpcName },
        }));
      } catch {
        // Edge cases: SSR/test env без window.CustomEvent. Игнорим.
      }
    }

    if (dbError) throw dbError;
    throw new Error(`[db/${rpcName}] unknown error: ${String(error)}`);
  }

  return data;
}

/**
 * isRaceError — utility для inline checks.
 *
 * Пример:
 *   try {
 *     await fn();
 *   } catch (err) {
 *     if (isRaceError(err, ['ORDER_TERMINAL'])) return;  // ignore
 *     throw err;
 *   }
 */
export function isRaceError(err: unknown, raceCodes: ErrCode[]): boolean {
  const dbError = err instanceof DbError ? err : parseDbError(err);
  if (!dbError) return false;
  return raceCodes.includes(dbError.code);
}

/**
 * Conservative race-swallow по RPC.
 *
 * ВНИМАНИЕ: глотаем ТОЛЬКО те ошибки которые ТОЧНО являются benign UX race
 * (конкурентные клики на одну операцию). Любая ошибка которая может быть
 * legitimate user-error → throw → UI должен её показать.
 *
 * НЕ swallow по умолчанию (было раньше, advisor забраковал):
 *   • CART_EMPTY на place_order — может быть UI bug (нажали "Оформить"
 *     при пустом cart). UI обязан показать "корзина пуста".
 *   • IDEMPOTENCY_IN_PROGRESS на add_payment — это «подожди», не «игнорируй».
 *     Если глотать → пользователь думает что не прошло → жмёт ещё раз →
 *     ДВОЙНОЙ платёж когда первый разлочится.
 *
 * Если в каком-то конкретном UI flow swallow CART_EMPTY действительно нужен
 * (например, optimistic update'нул cart и он реально мог опустеть),
 * передавай явно: `callRpc('place_order', args, {swallowRace: ['CART_EMPTY']})`.
 */
export const RPC_RACE_CODES: Record<string, ErrCode[]> = {
  // Повар B нажал ready после повара A — оба видят ready через realtime.
  // ORDER_TERMINAL = order уже в served/cancelled, B nажал поздно → no-op.
  // INVALID_KITCHEN_TRANSITION = B нажал на статус не-следующий-по-machine.
  update_kitchen_status: ['ORDER_TERMINAL', 'INVALID_KITCHEN_TRANSITION'],

  // Двойной клик "Принять"/"Отклонить" на transfer / share vote — second is no-op.
  reject_share_vote: ['NO_ACTIVE_SHARE_VOTE'],
  cancel_share_vote: ['NO_ACTIVE_SHARE_VOTE'],
  accept_line_transfer: ['LINE_HAS_PENDING_TRANSFER'],
  reject_line_transfer: ['LINE_HAS_PENDING_TRANSFER'],
};

/**
 * 00166 (оффлайн-касса): единая классификация network-сбоев — тот же паттерн,
 * что NETWORK-ветка addPayment (payments.ts): supabase-js v2 при обрыве сети
 * кидает TypeError (fetch) либо отдаёт error-поле с fetch/ERR_/abort в message.
 * Generic callRpc эти случаи НЕ различает (DbError UNKNOWN) — оффлайн-очередь
 * зовёт этот helper, чтобы отличить «сеть упала» (defer, попытка не сгорает)
 * от настоящей ошибки сервера. payments.ts намеренно не рефакторим — его
 * inline-логика load-bearing и покрыта тестами.
 */
export function isNetworkFailure(err: unknown): boolean {
  if (err instanceof TypeError) return true;
  const msg =
    err instanceof Error
      ? err.message
      : typeof err === "object" && err !== null && "message" in err
        ? String((err as { message: unknown }).message)
        : "";
  return /fetch|network|ERR_|abort/i.test(msg);
}

/**
 * Convenience: callRpc с авто-подбором swallowRace по имени RPC.
 *   await callRpcWithDefaults('update_kitchen_status', () => supabase.rpc(...))
 */
export async function callRpcWithDefaults<T>(
  rpcName: string,
  args: Record<string, unknown>,
  extraOptions: CallRpcOptions = {},
): Promise<T | null> {
  const defaultRace = RPC_RACE_CODES[rpcName] ?? [];
  const merged = [...defaultRace, ...(extraOptions.swallowRace ?? [])];
  return callRpc<T>(rpcName, args, { ...extraOptions, swallowRace: merged });
}
